package br.com.vvrefeicoes.portal.domain.model

import br.com.vvrefeicoes.portal.core.util.spreadsheet.SpreadsheetFormatException

/**
 * Definição de uma coluna de planilha do módulo RH. [header] é a chave normalizada (sem
 * acento/caixa) usada para casar com o cabeçalho importado; [label] é o texto exibido;
 * [editable] indica se a célula pode ser editada manualmente.
 */
interface RhSheetColumn {
    val header: String
    val label: String
    val editable: Boolean

    /** Outros nomes que a mesma coluna recebe nas planilhas exportadas pelos sistemas de folha e de ponto. */
    val aliases: List<String>
        get() = emptyList()

    /** Coluna sem a qual a planilha não é a desta aba: a importação é recusada se ela vier vazia. */
    val required: Boolean
        get() = false
}

/** Dados de uma planilha do RH, com as linhas alinhadas à ordem das colunas da aba. */
data class RhSheetData(
    val period: MonthPeriod,
    val rows: List<List<String>>,
    val costCenterIds: List<String?> = emptyList(),
)

/** Resultado da leitura da planilha: as linhas (mapas chaveados pelo header) e quem fez a última atualização. */
data class RhSheetLoad(
    val rows: List<Map<String, String>>,
    val updatedBy: String = "",
    val updatedAt: String = "",
)

/**
 * Converte a grade importada (primeira linha = cabeçalho) para [RhSheetData], projetando as
 * colunas na ordem de [columns]. Lança [SpreadsheetFormatException] se nenhuma coluna for
 * reconhecida.
 */
fun gridToRhSheet(
    grid: List<List<String>>,
    period: MonthPeriod,
    columns: List<RhSheetColumn>,
): RhSheetData {
    if (grid.isEmpty()) return RhSheetData(period, emptyList())
    val header = grid.first().map(::headerKey)
    val indexes = columns.map { column -> column.indexIn(header) }
    if (indexes.all { it < 0 }) {
        throw SpreadsheetFormatException("Não foi possível reconhecer as colunas da planilha")
    }
    val rows =
        grid
            .drop(1)
            .filter { row -> row.any { it.isNotBlank() } }
            .map { row ->
                columns.mapIndexed { index, _ ->
                    val position = indexes[index]
                    if (position in row.indices) row[position].trim() else ""
                }
            }
    return RhSheetData(period, rows)
}

/** Posição da coluna no cabeçalho importado, pelo nome principal ou por um dos [RhSheetColumn.aliases]. */
private fun RhSheetColumn.indexIn(header: List<String>): Int =
    (listOf(this.header) + aliases)
        .asSequence()
        .map { name -> header.indexOf(headerKey(name)) }
        .firstOrNull { it >= 0 } ?: -1

/**
 * Chave de comparação de cabeçalhos: além de acento e caixa, ignora pontuação, para que
 * "Raça/Cor", "RAÇA COR" e "Extras 50%" casem com as variações das planilhas. O "%" vira
 * palavra para não confundir "% Ausência" com "Ausência".
 */
internal fun headerKey(value: String): String =
    normalizeHeader(value)
        .replace("%", " percent ")
        .replace(Regex("[^a-z0-9]+"), " ")
        .trim()

internal fun normalizeHeader(value: String): String =
    buildString {
        value.trim().lowercase().forEach { char -> append(ACCENT_MAP[char] ?: char) }
    }.replace(Regex("\\s+"), " ")

private val ACCENT_MAP: Map<Char, Char> =
    mapOf(
        'á' to 'a',
        'à' to 'a',
        'â' to 'a',
        'ã' to 'a',
        'ä' to 'a',
        'é' to 'e',
        'è' to 'e',
        'ê' to 'e',
        'ë' to 'e',
        'í' to 'i',
        'ì' to 'i',
        'î' to 'i',
        'ï' to 'i',
        'ó' to 'o',
        'ò' to 'o',
        'ô' to 'o',
        'õ' to 'o',
        'ö' to 'o',
        'ú' to 'u',
        'ù' to 'u',
        'û' to 'u',
        'ü' to 'u',
        'ç' to 'c',
        'ñ' to 'n',
    )
